// frontend/src/pages/BOQPage.js

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, Grid, Card, CardContent,
  CardActions, Divider, Alert, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, InputAdornment, Paper
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Star as StarIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { siteAPI } from '../services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0' },
  approved: { label: 'Approved', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  final: { label: 'Final', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  deleted: { label: 'Deleted', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
};

const changeColors = {
  added: 'rgba(76, 175, 80, 0.10)',
  modified: 'rgba(255, 193, 7, 0.10)',
  removed: 'rgba(244, 67, 54, 0.10)',
  unchanged: 'transparent',
};

export default function BOQPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const canEdit = isAdmin || isManager;
  const isSupervisor = user?.role === 'supervisor';

  const siteIdParam = searchParams.get('siteId');
  const siteNameParam = searchParams.get('siteName');

  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(siteIdParam || '');
  const [boqs, setBoqs] = useState([]);
  const [multiBoqs, setMultiBoqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [managers, setManagers] = useState([]);
  const [boqSearch, setBoqSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');

  // View dialog
  const [viewOpen, setViewOpen] = useState(false);
  const [viewBoq, setViewBoq] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [hasEdits, setHasEdits] = useState(false);

  // Approve dialog
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveBoqId, setApproveBoqId] = useState(null);
  const [approveAction, setApproveAction] = useState('');
  const [approveData, setApproveData] = useState({ checkedById: '', approvedById: '' });

  // Download dialog
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadBoqId, setDownloadBoqId] = useState(null);

  // Save version dialog
  const [saveVersionOpen, setSaveVersionOpen] = useState(false);
  const [saveVersionBoqId, setSaveVersionBoqId] = useState(null);
  const [saveVersionApprovedBy, setSaveVersionApprovedBy] = useState('');
  const [saveVersionCheckedBy, setSaveVersionCheckedBy] = useState('');
  const [saveVersionNotes, setSaveVersionNotes] = useState('');
  const [savingVersion, setSavingVersion] = useState(false);

  // Delete dialog
  const [deleteBoqDialogOpen, setDeleteBoqDialogOpen] = useState(false);
  const [boqToDelete, setBoqToDelete] = useState(null);
  const [deletingBoq, setDeletingBoq] = useState(false);
  const [showBoqTrash, setShowBoqTrash] = useState(false);
  const [deletedBoqs, setDeletedBoqs] = useState([]);
  const [restoringBoq, setRestoringBoq] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchSites = useCallback(async () => { try { const r = await siteAPI.getAll(); setSites(r.data.data.sites); } catch (e) { } }, []);
  const fetchManagers = useCallback(async () => { try { const r = await axios.get(`${API_BASE_URL}/users?role=manager`, authHeaders()); setManagers(r.data.data?.users || []); } catch (e) { } }, []);
  const fetchBoqs = useCallback(async (siteId) => { if (!siteId) return; setLoading(true); setError(''); try { const r = await axios.get(`${API_BASE_URL}/boqs/site/${siteId}`, authHeaders()); setBoqs(r.data.data.boqs); } catch (e) { setError('Failed to load BOQs'); } finally { setLoading(false); } }, []);
  const fetchMultiBoqs = useCallback(async (siteId) => { if (!siteId) return; try { const r = await axios.get(`${API_BASE_URL}/boq-workbooks/site/${siteId}`, authHeaders()); setMultiBoqs(r.data.data.workbooks || []); } catch (e) { } }, []);

  useEffect(() => { fetchSites(); fetchManagers(); }, [fetchSites, fetchManagers]);
  useEffect(() => { if (selectedSiteId) { fetchBoqs(selectedSiteId); fetchMultiBoqs(selectedSiteId); } }, [selectedSiteId, fetchBoqs, fetchMultiBoqs]);

  const fetchBoqDetail = async (boqId) => { try { const r = await axios.get(`${API_BASE_URL}/boqs/${boqId}`, authHeaders()); setViewBoq(r.data.data.boq); setHasEdits(false); } catch (e) { setError('Failed to load BOQ details'); } };

  const handleDirectDownload = async (boqId) => {
    try {
      const boq = boqs.find(b => b.id === boqId) || viewBoq;
      const siteName = siteNameParam || sites.find(s => s.id === selectedSiteId)?.siteName || 'Site';
      const r = await axios.get(`${API_BASE_URL}/boqs/${boqId}/download`, { ...authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.setAttribute('download', `${boq?.versionLabel || 'BOQ'}_${siteName}.xlsx`);
      document.body.appendChild(a); a.click(); a.remove();
      setDownloadDialogOpen(false);
    } catch (e) { setError('Download failed'); }
  };

  const handleSaveAsNewVersion = async () => {
    if (!saveVersionApprovedBy) { setError('Please select Approved By manager'); return; }
    setSavingVersion(true); setError('');
    try {
      const r = await axios.post(`${API_BASE_URL}/boqs/${saveVersionBoqId}/create-version`, { siteId: selectedSiteId, approvedById: saveVersionApprovedBy, checkedById: saveVersionCheckedBy || null, notes: saveVersionNotes || null }, authHeaders());
      setSuccess(`${r.data.data.boq.versionLabel} saved!`);
      await handleDirectDownload(r.data.data.boq.id);
      fetchBoqs(selectedSiteId); setSaveVersionOpen(false); setSaveVersionApprovedBy(''); setSaveVersionCheckedBy(''); setSaveVersionNotes(''); setSaveVersionBoqId(null);
    } catch (e) { setError(e.response?.data?.message || 'Failed'); } finally { setSavingVersion(false); }
  };

  const handleSaveItem = async (itemId) => {
    try {
      await axios.put(`${API_BASE_URL}/boqs/item/${itemId}`, editValues, authHeaders());
      setSuccess('Item updated!'); setHasEdits(true);
      setViewBoq(prev => {
        if (!prev) return prev;
        const items = prev.items.map(item => {
          if (item.id !== itemId) return item;
          const u = { ...item };
          if (editValues.description !== undefined) u.description = editValues.description;
          if (editValues.unit !== undefined) u.unit = editValues.unit;
          if (editValues.quantity !== undefined) { u.previousQuantity = item.quantity; u.quantity = parseFloat(editValues.quantity); }
          if (editValues.rate !== undefined) { u.previousRate = item.rate; u.rate = parseFloat(editValues.rate); }
          if (editValues.amount !== undefined) { u.previousAmount = item.amount; u.amount = parseFloat(editValues.amount); }
          else if (editValues.quantity !== undefined && editValues.rate !== undefined) u.amount = parseFloat(editValues.quantity) * parseFloat(editValues.rate);
          if (editValues.quantity !== undefined || editValues.rate !== undefined) u.changeStatus = item.changeStatus === 'added' ? 'added' : 'modified';
          return u;
        });
        return { ...prev, items, totalAmount: items.filter(i => !i.isHeader).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0) };
      });
      setEditingItemId(null); setEditValues({}); fetchBoqs(selectedSiteId);
    } catch (e) { setError('Failed to update item'); }
  };

  const handleApprove = async () => {
    try { await axios.put(`${API_BASE_URL}/boqs/${approveBoqId}/approve`, { action: approveAction, ...approveData }, authHeaders()); setSuccess(`BOQ ${approveAction} successful!`); setApproveOpen(false); fetchBoqs(selectedSiteId); } catch (e) { setError('Action failed'); }
  };

  const handleDeleteBoq = async (type) => {
    if (!boqToDelete) return; setDeletingBoq(true);
    try {
      await axios.delete(`${API_BASE_URL}/boqs/${boqToDelete.id}?type=${type}`, authHeaders());
      setSuccess(type === 'hard' ? `${boqToDelete.versionLabel} permanently deleted!` : `${boqToDelete.versionLabel} moved to trash!`);
      setDeleteBoqDialogOpen(false); setBoqToDelete(null); fetchBoqs(selectedSiteId);
      if (showBoqTrash) fetchDeletedBoqs();
    } catch (e) { setError(e.response?.data?.message || 'Failed'); } finally { setDeletingBoq(false); }
  };

  const fetchDeletedBoqs = async () => { try { const r = await axios.get(`${API_BASE_URL}/boqs/trash?siteId=${selectedSiteId}`, authHeaders()); setDeletedBoqs(r.data.data.boqs || []); } catch (e) { } };

  const handleRestoreBoq = async (boqId, versionLabel) => {
    setRestoringBoq(true);
    try { await axios.put(`${API_BASE_URL}/boqs/${boqId}/restore`, {}, authHeaders()); setSuccess(`${versionLabel} restored!`); fetchDeletedBoqs(); fetchBoqs(selectedSiteId); }
    catch (e) { setError('Failed to restore'); } finally { setRestoringBoq(false); }
  };

  const selectedSiteName = siteNameParam || sites.find(s => s.id === selectedSiteId)?.siteName || '';
  const finalSingleBoq = boqs.find(b => b.status === 'final');
  const finalMultiBoq = multiBoqs.find(w => w.status === 'final');

  const filteredBoqs = boqs.filter(boq => {
    if (!boqSearch.trim()) return true;
    const q = boqSearch.toLowerCase();
    return boq.versionLabel?.toLowerCase().includes(q) || boq.status?.toLowerCase().includes(q) ||
      boq.approver?.name?.toLowerCase().includes(q) || boq.uploader?.name?.toLowerCase().includes(q) || boq.notes?.toLowerCase().includes(q);
  });

  const filteredMulti = multiBoqs.filter(wb => {
    if (!boqSearch.trim()) return true;
    const q = boqSearch.toLowerCase();
    return wb.versionLabel?.toLowerCase().includes(q) || wb.status?.toLowerCase().includes(q) ||
      wb.approver?.name?.toLowerCase().includes(q) || wb.uploader?.name?.toLowerCase().includes(q);
  });

  const StatusChip = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    return (
      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {cfg.label}
      </span>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8fafc' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .boq-root { font-family: 'Plus Jakarta Sans', sans-serif; }
        .boq-card { transition: box-shadow 0.2s, transform 0.2s; }
        .boq-card:hover { box-shadow: 0 8px 30px rgba(0,0,0,0.10) !important; transform: translateY(-2px); }
        .boq-card.final-card { box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important; }
        .action-btn { display:inline-flex; align-items:center; gap:5px; padding:6px 14px; border-radius:7px; font-size:0.78rem; font-weight:600; cursor:pointer; border:none; transition:all 0.15s; text-decoration:none; }
        .action-btn:hover { filter:brightness(0.93); }
        .btn-view     { background:#eff6ff; color:#2563eb; }
        .btn-download { background:#f0fdf4; color:#16a34a; }
        .btn-approve  { background:#f0fdf4; color:#16a34a; }
        .btn-final    { background:#fffbeb; color:#d97706; }
        .btn-delete   { background:#fef2f2; color:#dc2626; }
        .site-select-wrap select { appearance:none; }
      `}</style>

      <Box className="boq-root" sx={{ p: { xs: 2, md: 3 } }}>

        {/* ── Header ── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/sites')} size="small" sx={{ background: 'white', border: '1px solid #e2e8f0', '&:hover': { background: '#f1f5f9' } }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box>
              <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                BOQ Management
              </Typography>
              {selectedSiteName && (
                <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500, mt: 0.3 }}>
                  📍 {selectedSiteName}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {isAdmin && selectedSiteId && (
              <Button variant="outlined" size="small"
                onClick={() => { setShowBoqTrash(!showBoqTrash); if (!showBoqTrash) fetchDeletedBoqs(); }}
                sx={{ borderColor: '#fca5a5', color: '#dc2626', '&:hover': { borderColor: '#dc2626', background: '#fef2f2' }, fontWeight: 600, fontSize: '0.78rem', textTransform: 'none', borderRadius: '8px' }}>
                🗑 {showBoqTrash ? 'Hide Trash' : 'Trash'}
              </Button>
            )}
            {canEdit && selectedSiteId && (
              <Button variant="contained" startIcon={<UploadIcon />}
                onClick={() => navigate(`/boq-workbook?siteId=${selectedSiteId}&siteName=${encodeURIComponent(selectedSiteName)}`)}
                sx={{ background: 'white', color: '#2563eb', border: '1.5px solid #2563eb', boxShadow: 'none', fontWeight: 700, borderRadius: '10px', textTransform: 'none', px: 2.5, fontSize: '0.875rem', '&:hover': { background: '#eff6ff', boxShadow: 'none' } }}>
                Upload / Manage BOQ
              </Button>
            )}
          </Box>
        </Box>

        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setSuccess('')}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError('')}>{error}</Alert>}

        {/* ── Site Selector ── */}
        <Box sx={{ mb: 3, background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', p: 2.5, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
            Select Site
          </Typography>
          <TextField select fullWidth size="small" value={selectedSiteId} onChange={e => setSelectedSiteId(e.target.value)}
            placeholder="Choose a site to view BOQs"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', background: '#f8fafc', fontSize: '0.9rem', fontWeight: 500 } }}>
            <MenuItem value="" disabled><em style={{ color: '#94a3b8' }}>— Select a site —</em></MenuItem>
            {sites.map(s => (
              <MenuItem key={s.id} value={s.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontWeight: 600 }}>{s.siteName}</span>
                  <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>— {s.location}</span>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* ── Empty state ── */}
        {!selectedSiteId && (
          <Box sx={{ textAlign: 'center', py: 10, px: 4 }}>
            <Box sx={{ width: 80, height: 80, borderRadius: '20px', background: 'linear-gradient(135deg,#dbeafe,#eff6ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
              <FolderOpenIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
            </Box>
            <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#1e293b', mb: 1 }}>Select a site to view BOQs</Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem', maxWidth: 380, mx: 'auto' }}>
              Choose a site from the dropdown above to see all BOQ versions, upload new ones, and manage approvals.
            </Typography>
          </Box>
        )}

        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}

        {/* ── Final BOQ Banner ── */}
        {selectedSiteId && (finalSingleBoq || finalMultiBoq) && (
          <Box sx={{ mb: 3, p: 2.5, borderRadius: '14px', background: 'linear-gradient(135deg,rgba(245,158,11,0.12),rgba(245,158,11,0.06))', border: '1.5px solid rgba(245,158,11,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <StarIcon sx={{ color: '#f59e0b', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, color: '#92400e', fontSize: '0.95rem', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Final BOQ — {finalMultiBoq ? `${finalMultiBoq.versionLabel}` : `${finalSingleBoq?.versionLabel}`}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#b45309' }}>
                  Approved by: {finalMultiBoq ? finalMultiBoq.approver?.name || '-' : finalSingleBoq?.approver?.name || '-'}
                </Typography>
              </Box>
            </Box>
            <span style={{ fontSize: '0.72rem', fontWeight: 800, padding: '5px 14px', borderRadius: 99, background: '#f59e0b', color: 'white', letterSpacing: '0.08em' }}>⭐ FINAL</span>
          </Box>
        )}

        {/* ── BOQ List ── */}
        {!loading && selectedSiteId && (
          <>
            {(boqs.length > 0 || multiBoqs.length > 0) && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <TextField size="small" placeholder="Search BOQ versions..." value={boqSearch} onChange={e => setBoqSearch(e.target.value)}
                  sx={{ width: 260, '& .MuiOutlinedInput-root': { borderRadius: '10px', background: 'white' } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                    endAdornment: boqSearch && (<InputAdornment position="end"><IconButton size="small" onClick={() => setBoqSearch('')}><CloseIcon fontSize="small" /></IconButton></InputAdornment>)
                  }} />
              </Box>
            )}

            {boqs.length === 0 && multiBoqs.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10, background: 'white', borderRadius: '16px', border: '2px dashed #e2e8f0' }}>
                <Box sx={{ width: 72, height: 72, borderRadius: '18px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                  <AssignmentIcon sx={{ fontSize: 36, color: '#94a3b8' }} />
                </Box>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', mb: 1 }}>No BOQs uploaded yet</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem', mb: 3 }}>Upload the first BOQ for this site to get started.</Typography>
                {canEdit && (
                  <Button variant="contained" startIcon={<UploadIcon />}
                    onClick={() => navigate(`/boq-workbook?siteId=${selectedSiteId}&siteName=${encodeURIComponent(selectedSiteName)}`)}
                    sx={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', fontWeight: 700, borderRadius: '10px', textTransform: 'none', px: 3 }}>
                    Upload First BOQ
                  </Button>
                )}
              </Box>
            ) : (
              <Grid container spacing={2.5}>
                
                {/* Multi-Sheet BOQs */}
                {filteredMulti.map(wb => (
                  <Grid item xs={12} md={6} lg={4} key={`multi-${wb.id}`}>
                    <Box className={`boq-card${wb.status === 'final' ? ' final-card' : ''}`}
                      sx={{ background: 'white', borderRadius: '14px', border: `1px solid ${wb.status === 'final' ? '#f59e0b' : '#e2e8f0'}`, overflow: 'hidden', position: 'relative' }}>
                      <Box sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 42, height: 42, borderRadius: '10px', background: wb.status === 'final' ? 'rgba(245,158,11,0.1)' : '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <DescriptionIcon sx={{ color: wb.status === 'final' ? '#f59e0b' : '#3b82f6', fontSize: 22 }} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', fontFamily: 'Plus Jakarta Sans, sans-serif', lineHeight: 1.2 }}>{wb.versionLabel}</Typography>
                              <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.3 }}>
                                {wb.createdAt || wb.created_at ? new Date(wb.createdAt || wb.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Date unknown'} 
                              </Typography>
                            </Box>
                          </Box>
                          <StatusChip status={wb.status} />
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                          {(wb.sheets || []).map(s => (
                            <span key={s.id} style={{ fontSize: '0.7rem', fontWeight: 600, padding: '3px 9px', borderRadius: 6, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>{s.sheetName}</span>
                          ))}
                        </Box>

                        <Box sx={{ pt: 1.5, borderTop: '1px solid #f1f5f9' }}>
                          {wb.uploader && (
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                              <span style={{ color: '#94a3b8' }}>Uploaded by</span> <strong>{wb.uploader.name}</strong>
                            </Typography>
                          )}
                          {wb.approver && (
                            <Typography sx={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>✅ Approved by {wb.approver.name}</Typography>
                          )}
                          {wb.notes && (
                            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.5, fontStyle: 'italic' }}>📝 {wb.notes}</Typography>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ px: 2.5, pb: 2.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <button className="action-btn btn-view" onClick={() => navigate(`/boq-workbook?siteId=${selectedSiteId}&siteName=${encodeURIComponent(selectedSiteName)}`)}>
                          <VisibilityIcon sx={{ fontSize: 14 }} /> View / Edit
                        </button>
                      </Box>
                    </Box>
                  </Grid>
                ))}

                {/* Single BOQs */}
                {filteredBoqs.map(boq => (
                  <Grid item xs={12} md={6} lg={4} key={`single-${boq.id}`}>
                    <Box className={`boq-card${boq.status === 'final' ? ' final-card' : ''}`}
                      sx={{ background: 'white', borderRadius: '14px', border: `1px solid ${boq.status === 'final' ? '#f59e0b' : '#e2e8f0'}`, overflow: 'hidden', position: 'relative' }}>
                      <Box sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 42, height: 42, borderRadius: '10px', background: boq.status === 'final' ? 'rgba(245,158,11,0.1)' : '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <AssignmentIcon sx={{ color: boq.status === 'final' ? '#f59e0b' : '#16a34a', fontSize: 22 }} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', fontFamily: 'Plus Jakarta Sans, sans-serif', lineHeight: 1.2 }}>{boq.versionLabel}</Typography>
                              <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.3 }}>
                                {boq.createdAt || boq.created_at ? new Date(boq.createdAt || boq.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Date unknown'}
                              </Typography>
                            </Box>
                          </Box>
                          <StatusChip status={boq.status} />
                        </Box>

                        <Box sx={{ background: '#f8fafc', borderRadius: '10px', p: 1.5, mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Amount</Typography>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#2563eb', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                              ₹{Number(boq.totalAmount || 0).toLocaleString('en-IN')}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ pt: 1.5, borderTop: '1px solid #f1f5f9' }}>
                          {boq.uploader && (
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                              <span style={{ color: '#94a3b8' }}>Uploaded by</span> <strong>{boq.uploader.name}</strong>
                            </Typography>
                          )}
                          {boq.checker && (
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                              <span style={{ color: '#94a3b8' }}>Checked by</span> <strong>{boq.checker.name}</strong>
                            </Typography>
                          )}
                          {boq.approver && (
                            <Typography sx={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600, mb: 0.5 }}>✅ Approved by {boq.approver.name}</Typography>
                          )}
                          {boq.notes && (
                            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>📝 {boq.notes}</Typography>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ px: 2.5, pb: 2.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <button className="action-btn btn-view" onClick={() => { fetchBoqDetail(boq.id); setViewOpen(true); }}><VisibilityIcon sx={{ fontSize: 14 }} /> View</button>
                        <button className="action-btn btn-download" onClick={() => { setDownloadBoqId(boq.id); setDownloadDialogOpen(true); }}><DownloadIcon sx={{ fontSize: 14 }} /> Download</button>
                        {canEdit && boq.status === 'draft' && (
                          <button className="action-btn btn-approve" onClick={() => { setApproveBoqId(boq.id); setApproveAction('approve'); setApproveData({ checkedById: '', approvedById: '' }); setApproveOpen(true); }}><CheckCircleIcon sx={{ fontSize: 14 }} /> Approve</button>
                        )}
                        {canEdit && boq.status === 'approved' && (
                          <button className="action-btn btn-final" onClick={() => { setApproveBoqId(boq.id); setApproveAction('final'); setApproveData({ checkedById: '', approvedById: '' }); setApproveOpen(true); }}><StarIcon sx={{ fontSize: 14 }} /> Mark Final</button>
                        )}
                        {canEdit && (
                          <button className="action-btn btn-delete" onClick={() => { setBoqToDelete(boq); setDeleteBoqDialogOpen(true); }}><DeleteIcon sx={{ fontSize: 14 }} /> Delete</button>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Trash */}
            {showBoqTrash && isAdmin && (
              <Box sx={{ mt: 4 }}>
                <Typography sx={{ fontWeight: 700, color: '#dc2626', mb: 2, fontSize: '0.95rem' }}>🗑️ Deleted BOQs (Trash)</Typography>
                {deletedBoqs.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: '10px' }}>No deleted BOQs in trash.</Alert>
                ) : (
                  <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1.5px solid #fecaca', overflow: 'hidden' }}>
                    <Table size="small">
                      <TableHead sx={{ background: '#fef2f2' }}>
                        <TableRow>
                          {['Version', 'Site', 'Uploaded By', 'Total Amount', 'Restore'].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#dc2626' }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {deletedBoqs.map(boq => (
                          <TableRow key={boq.id}>
                            <TableCell><Typography variant="body2" fontWeight={600}>{boq.versionLabel}</Typography></TableCell>
                            <TableCell>{boq.site?.siteName}</TableCell>
                            <TableCell>{boq.uploader?.name || '-'}</TableCell>
                            <TableCell>₹{Number(boq.totalAmount || 0).toLocaleString('en-IN')}</TableCell>
                            <TableCell>
                              <button className="action-btn btn-approve" disabled={restoringBoq} onClick={() => handleRestoreBoq(boq.id, boq.versionLabel)}>Restore</button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* ── Approve Dialog ── */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {approveAction === 'approve' ? '✅ Approve BOQ' : '⭐ Mark as Final BOQ'}
        </DialogTitle>
        <DialogContent>
          {approveAction === 'approve' && (
            <>
              <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }}>Select the manager approving this BOQ.</Alert>
              <TextField select fullWidth label="Approved By *" value={approveData.approvedById} onChange={e => setApproveData(p => ({ ...p, approvedById: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                <MenuItem value="">-- Select Manager --</MenuItem>
                {managers.map(m => <MenuItem key={m.id} value={m.id}>{m.name} ({m.email})</MenuItem>)}
              </TextField>
              {approveData.approvedById && <Alert severity="success" sx={{ mt: 2, borderRadius: '10px' }}>✅ Approved by: <strong>{managers.find(m => m.id === approveData.approvedById)?.name}</strong></Alert>}
            </>
          )}
          {approveAction === 'final' && (
            <Alert severity="warning" sx={{ borderRadius: '10px' }}>Marking as Final is the official BOQ. Previous Final BOQs will revert to Approved. Supervisors can see this version.</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setApproveOpen(false)} sx={{ borderRadius: '9px', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color={approveAction === 'final' ? 'warning' : 'success'} onClick={handleApprove} disabled={approveAction === 'approve' && !approveData.approvedById} sx={{ borderRadius: '9px', fontWeight: 700, textTransform: 'none' }}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* ── View/Edit Dialog ── */}
      <Dialog open={viewOpen} onClose={() => { setViewOpen(false); setViewBoq(null); setEditingItemId(null); setHasEdits(false); setItemSearch(''); }} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AssignmentIcon sx={{ color: '#3b82f6' }} />
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {viewBoq?.versionLabel} — {viewBoq?.site?.siteName}
              </Typography>
              {viewBoq && <StatusChip status={viewBoq.status} />}
              {hasEdits && <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }}>Has edits</span>}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField size="small" placeholder="Search items..." value={itemSearch} onChange={e => setItemSearch(e.target.value)} sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: '9px' } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment> }} />
              <IconButton onClick={() => { setViewOpen(false); setViewBoq(null); setHasEdits(false); }}><CloseIcon /></IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {!viewBoq ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              {viewBoq.items?.some(i => i.changeStatus !== 'unchanged') && (
                <Box sx={{ display: 'flex', gap: 2, px: 3, py: 1.5, background: '#f8fafc', borderBottom: '1px solid #e2e8f0', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b' }}>Changes vs previous version:</Typography>
                  {[['★ New', '#dcfce7', '#16a34a'], ['✎ Modified', '#fef9c3', '#ca8a04'], ['✕ Removed', '#fee2e2', '#dc2626']].map(([label, bg, color]) => (
                    <span key={label} style={{ fontSize: '0.7rem', fontWeight: 700, padding: '3px 9px', borderRadius: 6, background: bg, color }}>{label}</span>
                  ))}
                </Box>
              )}
              <TableContainer sx={{ maxHeight: '60vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['S.No', 'Description', 'Unit', 'Quantity', ...(!isSupervisor ? ['Rate (₹)', 'Amount (₹)'] : []), ...(canEdit ? ['Edit'] : [])].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', background: '#f8fafc', color: '#475569', py: 1.5 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewBoq.items?.filter(item => !itemSearch.trim() || item.description?.toLowerCase().includes(itemSearch.toLowerCase()) || item.sno?.toLowerCase().includes(itemSearch.toLowerCase())).map(item => {
                      const isEditing = editingItemId === item.id;
                      if (item.isHeader) return (
                        <TableRow key={item.id} sx={{ background: '#f1f5f9' }}>
                          <TableCell colSpan={canEdit ? 7 : 6}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#2563eb' }}>{item.description}</Typography>
                          </TableCell>
                        </TableRow>
                      );
                      return (
                        <TableRow key={item.id} sx={{ background: changeColors[item.changeStatus] || 'transparent', '&:hover': { background: '#f8fafc' } }}>
                          <TableCell><Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{item.sno}</Typography></TableCell>
                          <TableCell>
                            {isEditing ? <TextField size="small" fullWidth multiline value={editValues.description ?? item.description} onChange={e => setEditValues(p => ({ ...p, description: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} /> : (
                              <Box>
                                <Typography sx={{ fontSize: '0.8rem' }}>{item.description}</Typography>
                                {item.changeStatus === 'modified' && item.previousAmount != null && (
                                  <Typography sx={{ fontSize: '0.7rem', color: '#d97706', mt: 0.3 }}>Was: Qty {item.previousQuantity} | Rate ₹{Number(item.previousRate).toLocaleString()} | Amt ₹{Number(item.previousAmount).toLocaleString()}</Typography>
                                )}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>{isEditing ? <TextField size="small" value={editValues.unit ?? item.unit} onChange={e => setEditValues(p => ({ ...p, unit: e.target.value }))} sx={{ width: 70, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} /> : item.unit}</TableCell>
                          <TableCell align="right">{isEditing ? <TextField size="small" type="number" value={editValues.quantity ?? item.quantity} onChange={e => setEditValues(p => ({ ...p, quantity: e.target.value }))} sx={{ width: 80, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} /> : Number(item.quantity).toLocaleString()}</TableCell>
                          {!isSupervisor && <TableCell align="right">{isEditing ? <TextField size="small" type="number" value={editValues.rate ?? item.rate} onChange={e => setEditValues(p => ({ ...p, rate: e.target.value }))} sx={{ width: 90, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} /> : Number(item.rate).toLocaleString()}</TableCell>}
                          {!isSupervisor && <TableCell align="right"><Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>₹{Number(item.amount).toLocaleString()}</Typography></TableCell>}
                          {canEdit && <TableCell align="center">
                            {isEditing ? (
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton size="small" color="success" onClick={() => handleSaveItem(item.id)}><SaveIcon fontSize="small" /></IconButton>
                                <IconButton size="small" onClick={() => { setEditingItemId(null); setEditValues({}); }}><CloseIcon fontSize="small" /></IconButton>
                              </Box>
                            ) : (
                              <IconButton size="small" onClick={() => { setEditingItemId(item.id); setEditValues({}); }}><EditIcon fontSize="small" /></IconButton>
                            )}
                          </TableCell>}
                        </TableRow>
                      );
                    })}
                    {!isSupervisor && (
                      <TableRow sx={{ background: '#f1f5f9' }}>
                        <TableCell colSpan={canEdit ? 5 : 4} align="right"><Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>TOTAL</Typography></TableCell>
                        <TableCell align="right"><Typography sx={{ fontWeight: 800, color: '#2563eb', fontSize: '0.95rem' }}>₹{Number(viewBoq.totalAmount || 0).toLocaleString('en-IN')}</Typography></TableCell>
                        {canEdit && <TableCell />}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {viewBoq && <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => { setViewOpen(false); setDownloadBoqId(viewBoq.id); setDownloadDialogOpen(true); }} sx={{ borderRadius: '9px', fontWeight: 700, textTransform: 'none' }}>{hasEdits ? 'Download / Save as New Version' : 'Download Excel'}</Button>}
          <Button onClick={() => { setViewOpen(false); setViewBoq(null); setHasEdits(false); }} sx={{ borderRadius: '9px', textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Download Dialog ── */}
      <Dialog open={downloadDialogOpen} onClose={() => setDownloadDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Download BOQ</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '0.9rem' }}>Have you made changes to this BOQ inside the app?</Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.85rem', mb: 2.5 }}>If yes — save as a new version first to keep full history.</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button variant="contained" onClick={() => { setDownloadDialogOpen(false); setSaveVersionBoqId(downloadBoqId); setSaveVersionOpen(true); }} fullWidth sx={{ borderRadius: '10px', fontWeight: 700, textTransform: 'none', py: 1.5 }}>✅ Yes — Save as New Version & Download</Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleDirectDownload(downloadBoqId)} fullWidth sx={{ borderRadius: '10px', fontWeight: 600, textTransform: 'none', py: 1.5 }}>No — Just Download Current Version</Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}><Button onClick={() => setDownloadDialogOpen(false)} sx={{ textTransform: 'none', borderRadius: '9px' }}>Cancel</Button></DialogActions>
      </Dialog>

      {/* ── Save Version Dialog ── */}
      <Dialog open={saveVersionOpen} onClose={() => setSaveVersionOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Save as New Version</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }}>This will create <strong>BOQ v{boqs.length + 1}</strong> with your changes.</Alert>
          {[['Checked By (Manager)', '', saveVersionCheckedBy, setSaveVersionCheckedBy, false], ['Approved By (Manager) *', 'Mandatory', saveVersionApprovedBy, setSaveVersionApprovedBy, true]].map(([label, helper, val, setter, required]) => (
            <TextField key={label} select fullWidth label={label} value={val} onChange={e => setter(e.target.value)} helperText={helper || ''} sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
              <MenuItem value="">{required ? '-- Select Manager --' : '-- Select Manager (Optional) --'}</MenuItem>
              {managers.map(m => <MenuItem key={m.id} value={m.id}>{m.name} — {m.email}</MenuItem>)}
            </TextField>
          ))}
          <TextField fullWidth multiline rows={2} label="Notes (Optional)" value={saveVersionNotes} onChange={e => setSaveVersionNotes(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setSaveVersionOpen(false)} sx={{ textTransform: 'none', borderRadius: '9px' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAsNewVersion} disabled={!saveVersionApprovedBy || savingVersion} startIcon={savingVersion ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />} sx={{ borderRadius: '9px', fontWeight: 700, textTransform: 'none' }}>{savingVersion ? 'Saving...' : 'Save & Download'}</Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Dialog ── */}
      <Dialog open={deleteBoqDialogOpen} onClose={() => setDeleteBoqDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Delete BOQ</DialogTitle>
        <DialogContent>
          {boqToDelete && (
            <Box sx={{ mb: 2, p: 2, background: '#f8fafc', borderRadius: '10px' }}>
              <Typography fontWeight={700}>{boqToDelete.versionLabel}</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>Total: ₹{Number(boqToDelete.totalAmount || 0).toLocaleString('en-IN')}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Alert severity="warning" sx={{ borderRadius: '10px' }}><strong>Soft Delete</strong> — BOQ moves to Trash. Can be restored later.</Alert>
            <Alert severity="error" sx={{ borderRadius: '10px' }}><strong>Hard Delete</strong> — Permanently deleted. Cannot be recovered.</Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteBoqDialogOpen(false)} sx={{ textTransform: 'none', borderRadius: '9px' }}>Cancel</Button>
          <Button variant="outlined" color="warning" disabled={deletingBoq} onClick={() => handleDeleteBoq('soft')} sx={{ textTransform: 'none', borderRadius: '9px', fontWeight: 700 }}>Soft Delete</Button>
          <Button variant="contained" color="error" disabled={deletingBoq} onClick={() => handleDeleteBoq('hard')} sx={{ textTransform: 'none', borderRadius: '9px', fontWeight: 700 }}>Hard Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}